import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { useCurrency } from '@/context/CurrencyContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { X, RefreshCw, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import VideoUploader from '@/components/VideoUploader';
import { uploadToCloudinary, uploadErrorMessage, IMAGE_TYPES, MAX_IMAGE_MB } from '@/lib/cloudinaryUpload';
import axios from 'axios';

import { toast } from 'sonner';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default exchange rates (fallback)
const DEFAULT_RATES = {
  USD: 1.00,
  EUR: 0.92,
  GBP: 0.79,
  NGN: 1550.00,
  ZAR: 18.50,
  AED: 3.67,
  INR: 83.50,
  CAD: 1.36,
  AUD: 1.53,
};

// Currency metadata
const CURRENCY_INFO = {
  USD: { symbol: '$', name: 'US Dollar' },
  EUR: { symbol: '€', name: 'Euro' },
  GBP: { symbol: '£', name: 'British Pound' },
  NGN: { symbol: '₦', name: 'Nigerian Naira' },
  ZAR: { symbol: 'R', name: 'South African Rand' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham' },
  INR: { symbol: '₹', name: 'Indian Rupee' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar' },
  AUD: { symbol: 'A$', name: 'Australian Dollar' },
};

// Predefined sizes for fashion
const AVAILABLE_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

// Predefined colors with hex values
const AVAILABLE_COLORS = [
  { name: 'Black', hex: '#000000' },
  { name: 'White', hex: '#FFFFFF' },
  { name: 'Gray', hex: '#6B7280' },
  { name: 'Red', hex: '#DC2626' },
  { name: 'Blue', hex: '#2563EB' },
  { name: 'Green', hex: '#16A34A' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Purple', hex: '#9333EA' },
  { name: 'Navy', hex: '#1E3A5F' },
  { name: 'Brown', hex: '#92400E' },
  { name: 'Beige', hex: '#D4C4A8' },
];

const ProductForm = ({ formData, onChange, onSubmit, onCancel, saving, isEditing }) => {
  const { currency: userCurrency } = useCurrency();
  const [priceCurrency, setPriceCurrency] = useState(userCurrency || 'USD');
  const [localPrice, setLocalPrice] = useState('');
  const [exchangeRates, setExchangeRates] = useState(DEFAULT_RATES);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesLastUpdated, setRatesLastUpdated] = useState(null);
  
  // Variant state
  const [hasVariants, setHasVariants] = useState(formData.has_variants || false);
  const [selectedSizes, setSelectedSizes] = useState(formData.available_sizes || []);
  const [selectedColors, setSelectedColors] = useState(formData.available_colors || []);
  const [variantStocks, setVariantStocks] = useState(formData.variants || []);
  
  // Image upload state
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(formData.image_url || '');
  const [additionalImages, setAdditionalImages] = useState(formData.images || []);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);
  
  // Video state
  const [videos, setVideos] = useState(formData.videos || []);
  const [newVideoUrl, setNewVideoUrl] = useState('');

  // Upload images straight to Cloudinary (permanent storage)
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, WebP or HEIC image.');
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(`Image is too large. Maximum size is ${MAX_IMAGE_MB}MB.`);
      return;
    }
    setUploading(true);
    try {
      const imageUrl = await uploadToCloudinary(file);
      onChange('image_url', imageUrl);
      setImagePreview(imageUrl);
    } catch (error) {
      toast.error(uploadErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    if (additionalImages.length >= 5) {
      toast.error('Maximum 5 additional images allowed.');
      return;
    }
    setUploadingAdditional(true);
    const newImages = [...additionalImages];
    let failed = 0;
    for (const file of files) {
      if (newImages.length >= 5) break;
      if (!IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_MB * 1024 * 1024) { failed++; continue; }
      try {
        newImages.push(await uploadToCloudinary(file));
      } catch {
        failed++;
      }
    }
    setAdditionalImages(newImages);
    onChange('images', newImages);
    if (failed) toast.error(`${failed} image(s) could not be uploaded.`);
    setUploadingAdditional(false);
  };

  // Remove additional image
  const removeAdditionalImage = (index) => {
    const newImages = additionalImages.filter((_, i) => i !== index);
    setAdditionalImages(newImages);
    onChange('images', newImages);
  };

  // Fetch live exchange rates
  const fetchExchangeRates = useCallback(async () => {
    setRatesLoading(true);
    try {
      // Using exchangerate-api.com free tier (no API key needed for basic usage)
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await response.json();
      
      if (data.result === 'success' && data.rates) {
        const newRates = {};
        Object.keys(CURRENCY_INFO).forEach(code => {
          newRates[code] = data.rates[code] || DEFAULT_RATES[code];
        });
        setExchangeRates(newRates);
        setRatesLastUpdated(new Date());
        
        // Save to localStorage for persistence
        localStorage.setItem('salesspace_exchange_rates', JSON.stringify({
          rates: newRates,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      // Use cached rates if available
      const cached = localStorage.getItem('salesspace_exchange_rates');
      if (cached) {
        const { rates } = JSON.parse(cached);
        setExchangeRates(rates);
      }
    } finally {
      setRatesLoading(false);
    }
  }, []);

  // Load exchange rates on mount
  useEffect(() => {
    // Check localStorage for cached rates
    const cached = localStorage.getItem('salesspace_exchange_rates');
    if (cached) {
      const { rates, timestamp } = JSON.parse(cached);
      const hoursSinceUpdate = (Date.now() - timestamp) / (1000 * 60 * 60);
      
      if (hoursSinceUpdate < 1) {
        // Use cached rates if less than 1 hour old
        setExchangeRates(rates);
        setRatesLastUpdated(new Date(timestamp));
        return;
      }
    }
    
    // Fetch fresh rates
    fetchExchangeRates();
  }, [fetchExchangeRates]);

  // Initialize local price when editing or currency changes
  useEffect(() => {
    if (formData.price) {
      const usdPrice = parseFloat(formData.price);
      const rate = exchangeRates[priceCurrency] || 1;
      const converted = usdPrice * rate;
      setLocalPrice(converted.toFixed(2));
    }
  }, [priceCurrency, exchangeRates]);

  // Initialize variant state from formData when editing
  useEffect(() => {
    if (isEditing) {
      setHasVariants(formData.has_variants || false);
      setSelectedSizes(formData.available_sizes || []);
      setSelectedColors(formData.available_colors || []);
      setVariantStocks(formData.variants || []);
    }
  }, [isEditing, formData.has_variants, formData.available_sizes, formData.available_colors, formData.variants]);

  // Generate variant combinations and sync stocks
  useEffect(() => {
    if (hasVariants && selectedSizes.length > 0 && selectedColors.length > 0) {
      // Generate all combinations
      const newVariants = [];
      selectedSizes.forEach(size => {
        selectedColors.forEach(color => {
          // Check if this combination already exists in variantStocks
          const existing = variantStocks.find(v => v.size === size && v.color === color.name);
          newVariants.push({
            size,
            color: color.name,
            stock: existing?.stock || 0
          });
        });
      });
      setVariantStocks(newVariants);
      onChange('variants', newVariants);
    }
  }, [selectedSizes, selectedColors, hasVariants]);

  // Update variant stock
  const updateVariantStock = (size, colorName, stock) => {
    const newVariants = variantStocks.map(v => {
      if (v.size === size && v.color === colorName) {
        return { ...v, stock: parseInt(stock) || 0 };
      }
      return v;
    });
    setVariantStocks(newVariants);
    onChange('variants', newVariants);
  };

  // Convert local price to USD when price changes
  const handlePriceChange = (value) => {
    setLocalPrice(value);
    const localAmount = parseFloat(value) || 0;
    const rate = exchangeRates[priceCurrency] || 1;
    const usdAmount = localAmount / rate;
    onChange('price', usdAmount.toFixed(2));
  };

  // Handle currency change
  const handleCurrencyChange = (newCurrency) => {
    const usdPrice = parseFloat(formData.price) || 0;
    const newRate = exchangeRates[newCurrency] || 1;
    const newLocalPrice = usdPrice * newRate;
    setLocalPrice(newLocalPrice.toFixed(2));
    setPriceCurrency(newCurrency);
  };

  // Toggle size selection
  const toggleSize = (size) => {
    const newSizes = selectedSizes.includes(size)
      ? selectedSizes.filter(s => s !== size)
      : [...selectedSizes, size];
    setSelectedSizes(newSizes);
    onChange('available_sizes', newSizes);
  };

  // Toggle color selection
  const toggleColor = (color) => {
    const exists = selectedColors.find(c => c.name === color.name);
    const newColors = exists
      ? selectedColors.filter(c => c.name !== color.name)
      : [...selectedColors, color];
    setSelectedColors(newColors);
    onChange('available_colors', newColors);
  };

  // Handle variant toggle
  const handleVariantToggle = (enabled) => {
    setHasVariants(enabled);
    onChange('has_variants', enabled);
    if (!enabled) {
      setSelectedSizes([]);
      setSelectedColors([]);
      onChange('available_sizes', []);
      onChange('available_colors', []);
    }
  };

  const currencySymbol = CURRENCY_INFO[priceCurrency]?.symbol || '$';
  const showVariantOption = formData.product_type === 'physical' && 
    ['Fashion', 'Accessories', 'Sports'].includes(formData.category);

  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="name">Product Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            required
            data-testid="product-name-input"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="price">Price *</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={fetchExchangeRates}
              disabled={ratesLoading}
              className="h-6 text-xs gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${ratesLoading ? 'animate-spin' : ''}`} />
              {ratesLastUpdated ? 'Refresh rates' : 'Load live rates'}
            </Button>
          </div>
          <div className="flex gap-2">
            <Select value={priceCurrency} onValueChange={handleCurrencyChange}>
              <SelectTrigger className="w-28" data-testid="price-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CURRENCY_INFO).map(([code, { symbol }]) => (
                  <SelectItem key={code} value={code}>
                    {symbol} {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="price"
              type="number"
              step="0.01"
              min="0"
              value={localPrice}
              onChange={(e) => handlePriceChange(e.target.value)}
              placeholder={`Price in ${priceCurrency}`}
              required
              className="flex-1 min-w-[150px] text-lg font-medium"
              data-testid="product-price-input"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            ≈ ${parseFloat(formData.price || 0).toFixed(2)} USD
            {ratesLastUpdated && (
              <span className="ml-1 text-green-600">
                (Live rate: 1 USD = {exchangeRates[priceCurrency]?.toFixed(2)} {priceCurrency})
              </span>
            )}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => onChange('category', value)}
          >
            <SelectTrigger data-testid="product-category-input">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Electronics">Electronics</SelectItem>
              <SelectItem value="Fashion">Fashion</SelectItem>
              <SelectItem value="Home & Garden">Home & Garden</SelectItem>
              <SelectItem value="Sports">Sports</SelectItem>
              <SelectItem value="Beauty">Beauty</SelectItem>
              <SelectItem value="Software">Software</SelectItem>
              <SelectItem value="Courses">Courses</SelectItem>
              <SelectItem value="Accessories">Accessories</SelectItem>
              <SelectItem value="Services">Services</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-2 space-y-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => onChange('description', e.target.value)}
            rows={3}
            required
            data-testid="product-description-input"
          />
        </div>

        <div className="col-span-2 space-y-2">
          <Label htmlFor="image">Main Product Image *</Label>
          <div className="flex gap-4">
            {/* Image Preview */}
            <div className="w-32 h-32 border rounded-lg overflow-hidden bg-muted flex items-center justify-center shrink-0">
              {imagePreview || formData.image_url ? (
                <img 
                  src={imagePreview || formData.image_url} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`flex-col items-center justify-center text-muted-foreground ${imagePreview || formData.image_url ? 'hidden' : 'flex'}`}>
                <ImageIcon className="w-8 h-8 mb-1" />
                <span className="text-xs">No image</span>
              </div>
            </div>
            
            {/* Upload Options */}
            <div className="flex-1 space-y-3">
              {/* Upload Button */}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="image-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="gap-2"
                  data-testid="upload-image-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload Image
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG, WebP, HEIC · Max 20MB
                </p>
              </div>
              
              {/* Or URL Input */}
              <div className="space-y-1">
                <Label htmlFor="image_url" className="text-xs text-muted-foreground">
                  Or paste image URL:
                </Label>
                <Input
                  id="image_url"
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => {
                    onChange('image_url', e.target.value);
                    setImagePreview(e.target.value);
                  }}
                  placeholder="https://example.com/image.jpg"
                  data-testid="product-image-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Images Gallery */}
        <div className="col-span-2 space-y-2">
          <Label>Additional Images (up to 5)</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Add more product images for a gallery view
          </p>
          
          <div className="flex flex-wrap gap-3">
            {/* Existing additional images */}
            {additionalImages.map((img, index) => (
              <div key={index} className="relative w-24 h-24 border rounded-lg overflow-hidden group" data-testid={`additional-image-${index}`}>
                <img 
                  src={img} 
                  alt={`Product ${index + 2}`} 
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  data-testid={`remove-additional-image-${index}`}
                  onClick={() => removeAdditionalImage(index)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            {/* Add more button */}
            {additionalImages.length < 5 && (
              <div>
                <input
                  ref={additionalFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAdditionalImageUpload}
                  multiple
                  className="hidden"
                  data-testid="additional-images-input"
                />
                <button
                  type="button"
                  data-testid="add-more-images-btn"
                  onClick={() => additionalFileInputRef.current?.click()}
                  disabled={uploadingAdditional}
                  className="w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                >
                  {uploadingAdditional ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 mb-1" />
                      <span className="text-xs">Add More</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Product Videos */}
        <div className="col-span-2 space-y-2">
          <Label>Product Videos</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Upload a video file (up to 100MB) or paste a YouTube / MP4 link. Max 3 videos.
          </p>
          
          {/* Existing videos */}
          <div className="space-y-2 mb-3">
            {videos.map((video, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded-lg" data-testid={`video-item-${index}`}>
                <span className="text-xs truncate flex-1">{video}</span>
                <button
                  type="button"
                  data-testid={`remove-video-${index}`}
                  onClick={() => {
                    const newVideos = videos.filter((_, i) => i !== index);
                    setVideos(newVideos);
                    onChange('videos', newVideos);
                  }}
                  className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Add new video */}
          {videos.length < 3 && (
            <VideoUploader
              className="w-full mb-2 hover:bg-muted"
              onUploaded={(url) => {
                setVideos((prev) => {
                  const newVideos = [...prev, url];
                  onChange('videos', newVideos);
                  return newVideos;
                });
              }}
            />
          )}
          {videos.length < 3 && (
            <div className="flex gap-2">
              <Input
                placeholder="https://youtube.com/watch?v=... or video.mp4"
                value={newVideoUrl}
                onChange={(e) => setNewVideoUrl(e.target.value)}
                className="flex-1"
                data-testid="video-url-input"
              />
              <Button
                type="button"
                variant="outline"
                data-testid="add-video-url-btn"
                onClick={() => {
                  if (newVideoUrl.trim()) {
                    const newVideos = [...videos, newVideoUrl.trim()];
                    setVideos(newVideos);
                    onChange('videos', newVideos);
                    setNewVideoUrl('');
                  }
                }}
                disabled={!newVideoUrl.trim()}
              >
                Add Video
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="product_type">Product Type *</Label>
          <Select
            value={formData.product_type}
            onValueChange={(value) => onChange('product_type', value)}
          >
            <SelectTrigger data-testid="product-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="physical">Physical Product</SelectItem>
              <SelectItem value="affiliate">Affiliate Product</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.product_type === 'physical' ? (
          <div className="space-y-2">
            <Label htmlFor="stock">Stock Quantity</Label>
            <Input
              id="stock"
              type="number"
              min="0"
              value={formData.stock}
              onChange={(e) => onChange('stock', e.target.value)}
              className="text-lg font-medium"
              data-testid="product-stock-input"
            />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="commission_rate">Commission Rate (%)</Label>
              <Input
                id="commission_rate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.commission_rate}
                onChange={(e) => onChange('commission_rate', e.target.value)}
                data-testid="product-commission-input"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="affiliate_url">Affiliate URL *</Label>
              <Input
                id="affiliate_url"
                type="url"
                value={formData.affiliate_url}
                onChange={(e) => onChange('affiliate_url', e.target.value)}
                placeholder="https://..."
                required={formData.product_type === 'affiliate'}
                data-testid="product-affiliate-url-input"
              />
            </div>
          </>
        )}

        {/* Variant Options - Only show for Fashion/Accessories/Sports physical products */}
        {showVariantOption && (
          <div className="col-span-2 space-y-4 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Product Variants (Optional)</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Add size and color options for this product
                </p>
              </div>
              <Switch
                id="has_variants"
                checked={hasVariants}
                onCheckedChange={handleVariantToggle}
                data-testid="has-variants-switch"
              />
            </div>

            {hasVariants && (
              <div className="space-y-4 pt-2">
                {/* Size Selection */}
                <div className="space-y-2">
                  <Label className="text-sm">Available Sizes</Label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_SIZES.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => toggleSize(size)}
                        className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                          selectedSizes.includes(size)
                            ? 'bg-foreground text-background border-foreground'
                            : 'bg-background hover:bg-muted border-border'
                        }`}
                        data-testid={`size-toggle-${size}`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  {selectedSizes.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Selected: {selectedSizes.join(', ')}
                    </p>
                  )}
                </div>

                {/* Color Selection */}
                <div className="space-y-2">
                  <Label className="text-sm">Available Colors</Label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_COLORS.map((color) => {
                      const isSelected = selectedColors.some(c => c.name === color.name);
                      return (
                        <button
                          key={color.name}
                          type="button"
                          onClick={() => toggleColor(color)}
                          className={`relative w-8 h-8 rounded-full border-2 transition-all ${
                            isSelected
                              ? 'ring-2 ring-foreground ring-offset-2 border-foreground'
                              : 'border-border hover:border-foreground/50'
                          }`}
                          style={{ backgroundColor: color.hex }}
                          title={color.name}
                          data-testid={`color-toggle-${color.name}`}
                        >
                          {color.hex === '#FFFFFF' && (
                            <span className="absolute inset-0 rounded-full border border-gray-300" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {selectedColors.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedColors.map((color) => (
                        <Badge 
                          key={color.name} 
                          variant="secondary"
                          className="gap-1 pr-1"
                        >
                          <span 
                            className="w-3 h-3 rounded-full border"
                            style={{ backgroundColor: color.hex }}
                          />
                          {color.name}
                          <button
                            type="button"
                            onClick={() => toggleColor(color)}
                            className="ml-1 hover:bg-muted rounded"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Variant Stock Editor */}
                {selectedSizes.length > 0 && selectedColors.length > 0 && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label className="text-sm font-semibold">Stock per Variant</Label>
                    <p className="text-xs text-muted-foreground">
                      Set inventory for each size/color combination
                    </p>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-background">
                          <tr className="border-b">
                            <th className="text-left py-2 px-2 font-medium">Size</th>
                            <th className="text-left py-2 px-2 font-medium">Color</th>
                            <th className="text-right py-2 px-2 font-medium w-24">Stock</th>
                          </tr>
                        </thead>
                        <tbody>
                          {variantStocks.map((variant, idx) => {
                            const colorInfo = selectedColors.find(c => c.name === variant.color);
                            return (
                              <tr key={`${variant.size}-${variant.color}`} className="border-b border-dashed">
                                <td className="py-2 px-2">{variant.size}</td>
                                <td className="py-2 px-2">
                                  <div className="flex items-center gap-2">
                                    <span 
                                      className="w-4 h-4 rounded-full border"
                                      style={{ backgroundColor: colorInfo?.hex || '#ccc' }}
                                    />
                                    {variant.color}
                                  </div>
                                </td>
                                <td className="py-2 px-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    value={variant.stock}
                                    onChange={(e) => updateVariantStock(variant.size, variant.color, e.target.value)}
                                    className="w-24 h-9 text-center text-base font-medium"
                                    data-testid={`variant-stock-${variant.size}-${variant.color}`}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-muted-foreground text-right">
                      Total: {variantStocks.reduce((sum, v) => sum + (v.stock || 0), 0)} units
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="col-span-2 space-y-2">
          <Label htmlFor="tags">Tags (comma separated)</Label>
          <Input
            id="tags"
            value={formData.tags}
            onChange={(e) => onChange('tags', e.target.value)}
            placeholder="tech, gadget, wireless"
            data-testid="product-tags-input"
          />
        </div>

        <div className="col-span-2 flex items-center gap-2">
          <Switch
            id="featured"
            checked={formData.featured}
            onCheckedChange={(checked) => onChange('featured', checked)}
            data-testid="product-featured-switch"
          />
          <Label htmlFor="featured">Featured Product</Label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} data-testid="save-product-btn">
          {saving ? 'Saving...' : isEditing ? 'Update Product' : 'Create Product'}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
